// Firebase modular SDK init (replaces the legacy `firebase-client.jsx`
// compat-UMD setup). Lazily initializes on first call so any module that
// imports `getFirebaseAuth()` gets a ready Auth instance regardless of
// import order.
//
// Persistence: browser-local IndexedDB (the legacy app set this explicitly;
// keeping it explicit on audit). Phone OTP defaults to local but pinning
// it makes intent clear.
//
// Config values are PUBLIC (they identify the project, they don't
// authenticate). Safe to commit. Real gate is server-side rules + Admin
// SDK ID-token verification.

import { getApps, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  type Auth,
} from 'firebase/auth';

let cachedAuth: Auth | null = null;

function ensureFirebaseApp(): void {
  if (getApps().length > 0) return;
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  if (!apiKey) {
    // Loud rather than silent — the legacy app hardcoded its config so it
    // always "worked"; with env vars, an unconfigured dev gets a clear
    // signal here instead of a cryptic Firebase error five frames deep.
    throw new Error(
      'Firebase config missing — set VITE_FIREBASE_* in apps/web/.env.local. ' +
        'See apps/web/.env.example for the full list.',
    );
  }
  initializeApp({
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  });
}

export function getFirebaseAuth(): Auth {
  if (cachedAuth) return cachedAuth;
  ensureFirebaseApp();
  const auth = getAuth();
  // Local development only. When VITE_FIREBASE_AUTH_EMULATOR_HOST is set
  // (e.g. `127.0.0.1:9099`), route Auth to the Firebase Auth emulator so
  // e-mail/password sign-in works against local test users; transcriber-api accepts those tokens when
  // it runs with FIREBASE_AUTH_EMULATOR_HOST pointing at the same emulator.
  // Unset (the default, and always in production builds) → no call, no
  // behaviour change. Storage is NOT routed to an emulator.
  const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
  if (typeof emulatorHost === 'string' && emulatorHost.trim() !== '') {
    connectAuthEmulator(auth, `http://${emulatorHost.trim()}`, { disableWarnings: true });
  }
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.warn('firebase setPersistence failed', err);
  });
  cachedAuth = auth;
  return auth;
}

/**
 * Storage endpoint for the REST resumable uploader (utils/resumable-upload).
 * The Storage SDK is no longer bundled; uploads talk to
 * firebasestorage.googleapis.com directly with the user's ID token.
 * VITE_FIREBASE_STORAGE_EMULATOR_HOST (local dev only) points the same
 * calls at the Storage emulator.
 */
export function getStorageConfig(): { bucket: string; baseUrl: string } {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  if (!bucket) {
    throw new Error(
      'Firebase Storage bucket missing — set VITE_FIREBASE_STORAGE_BUCKET in apps/web/.env.local.',
    );
  }
  const emu = import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_HOST;
  const baseUrl =
    typeof emu === 'string' && emu.trim() !== ''
      ? `http://${emu.trim()}`
      : 'https://firebasestorage.googleapis.com';
  return { bucket, baseUrl };
}
